/**
 * Project Store - Zustand state management for projects
 */
import { create } from 'zustand';
import { Project } from '../types';
import { api } from '../services/api';
import { getErrorMessage } from '../utils/errorMessages';

interface ProjectState {
  // State
  projects: Project[];
  currentProject: Project | null;
  loading: boolean;
  error: string | null;

  // Actions
  fetchProjects: () => Promise<void>;
  fetchProject: (id: number) => Promise<void>;
  createProject: (data: { name: string; description?: string }) => Promise<Project>;
  updateProject: (id: number, data: Partial<Project>) => Promise<void>;
  deleteProject: (id: number) => Promise<void>;
  duplicateProject: (id: number, options?: any) => Promise<Project>;
  setCurrentProject: (project: Project | null) => void;
  clearError: () => void;

  // Permission checks
  canView: () => boolean;
  canEdit: () => boolean;
  canDelete: () => boolean;
  isOwner: () => boolean;
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  // Initial state
  projects: [],
  currentProject: null,
  loading: false,
  error: null,

  // Fetch all projects
  fetchProjects: async () => {
    set({ loading: true, error: null });
    try {
      const response = await api.projects.getAll();
      set({ projects: response.data, loading: false });
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Nepodarilo sa načítať projekty.'), loading: false });
    }
  },

  // Fetch single project
  fetchProject: async (id: number) => {
    set({ loading: true, error: null });
    try {
      const response = await api.projects.getById(id);
      set({ currentProject: response.data, loading: false });
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Nepodarilo sa načítať projekt.'), loading: false });
    }
  },

  // Create project
  createProject: async (data) => {
    set({ loading: true, error: null });
    try {
      const response = await api.projects.create(data);
      const project = response.data;
      set((state) => ({
        projects: [...state.projects, project],
        currentProject: project,
        loading: false,
      }));
      return project;
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Nepodarilo sa vytvoriť projekt.'), loading: false });
      throw error;
    }
  },

  // Update project
  updateProject: async (id, data) => {
    set({ loading: true, error: null });
    try {
      const response = await api.projects.update(id, data);
      const updated = response.data;
      set((state) => ({
        projects: state.projects.map((p) => (p.id === id ? updated : p)),
        currentProject: state.currentProject?.id === id ? updated : state.currentProject,
        loading: false,
      }));
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Nepodarilo sa uložiť zmeny projektu.'), loading: false });
      throw error;
    }
  },

  // Delete project
  deleteProject: async (id) => {
    set({ loading: true, error: null });
    try {
      await api.projects.delete(id);
      set((state) => ({
        projects: state.projects.filter((p) => p.id !== id),
        currentProject: state.currentProject?.id === id ? null : state.currentProject,
        loading: false,
      }));
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Nepodarilo sa zmazať projekt.'), loading: false });
      throw error;
    }
  },

  // Duplicate project
  duplicateProject: async (id, options) => {
    set({ loading: true, error: null });
    try {
      const response = await api.projects.duplicate(id, options);
      const duplicated = response.data;
      set((state) => ({
        projects: [...state.projects, duplicated],
        loading: false,
      }));
      return duplicated;
    } catch (error: any) {
      set({ error: getErrorMessage(error, 'Nepodarilo sa duplikovať projekt.'), loading: false });
      throw error;
    }
  },

  // Set current project
  setCurrentProject: (project) => {
    set({ currentProject: project });
  },

  // Clear error
  clearError: () => {
    set({ error: null });
  },

  // Permission checks
  canView: () => {
    const { currentProject } = get();
    if (!currentProject) return false;
    // null = admin (full access), viewer/editor/owner = has access
    return currentProject.user_permission !== undefined;
  },

  canEdit: () => {
    const { currentProject } = get();
    if (!currentProject) return false;
    // null = admin, editor or owner can edit
    return currentProject.user_permission === null ||
           currentProject.user_permission === 'editor' ||
           currentProject.user_permission === 'owner';
  },

  canDelete: () => {
    const { currentProject } = get();
    if (!currentProject) return false;
    // null = admin, only owner can delete
    return currentProject.user_permission === null ||
           currentProject.user_permission === 'owner';
  },

  isOwner: () => {
    const { currentProject } = get();
    if (!currentProject) return false;
    // null = admin (acts as owner), owner = owner
    return currentProject.user_permission === null ||
           currentProject.user_permission === 'owner';
  },
}));

